import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// Generic region load map — abstract dashed grid, not tied to any country.
// Bubble size ∝ selected metric (registrations / fraud / revoked). Bubbles are
// rendered inside a neutral rounded rectangle so the same widget works for any
// jurisdiction the platform is deployed in.
const OUTLINE = "5,10 95,10 95,90 5,90";

export default function RegionMap() {
    const { t, lang } = useI18n();
    const nav = useNavigate();
    const [data, setData] = useState([]);
    const [hover, setHover] = useState(null);
    const [mode, setMode] = useState("all"); // all | fraud | revoked
    useEffect(() => {
        api.get("/reports/governorate").then((r) => setData(r.data.governorates));
    }, []);

    if (!data.length) return null;
    const nameOf = (g) => (lang === "ar" ? g.name_ar : lang === "fr" ? (g.name_fr || g.name_en) : g.name_en);
    const metric = (g) => mode === "fraud" ? g.fraud_flags : mode === "revoked" ? g.revoked_plates : g.registrations;
    const max = Math.max(1, ...data.map(metric));
    const modeLabel =
        mode === "fraud" ? (lang === "ar" ? "إشارات الاحتيال" : lang === "fr" ? "Signalements" : "Fraud flags") :
        mode === "revoked" ? (lang === "ar" ? "الملصقات الملغاة" : lang === "fr" ? "Vignettes révoquées" : "Revoked stickers") :
        (lang === "ar" ? "التسجيلات" : lang === "fr" ? "Immatriculations" : "Registrations");
    const modeColor = mode === "fraud" ? "#f59e0b" : mode === "revoked" ? "#111111" : "#C8102E";

    return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6" data-testid="region-map-card">
            <div className="relative bg-[#0f0f10] rounded-sm p-4 border border-border overflow-hidden">
                <div className="grain absolute inset-0" />
                <div className="relative z-10 flex items-center justify-between mb-1">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-bold">{t("region")} · {lang === "ar" ? "الحمل" : lang === "fr" ? "Charge" : "Load"}</div>
                        <div className="text-xs text-white/40 mono">bubble ∝ {modeLabel.toLowerCase()}</div>
                    </div>
                    <div className="inline-flex rounded-sm border border-white/20 bg-white/5 text-[10px] tracking-widest uppercase" data-testid="map-mode-tabs">
                        {["all", "fraud", "revoked"].map((m) => (
                            <button key={m} onClick={() => setMode(m)} className={`px-2.5 py-1 ${mode === m ? "bg-white text-black" : "text-white/70 hover:text-white"}`} data-testid={`map-mode-${m}`}>
                                {m === "all" ? t("dashboard") && (lang === "ar" ? "التسجيلات" : lang === "fr" ? "Immat." : "Reg.") : m === "fraud" ? (lang === "ar" ? "احتيال" : lang === "fr" ? "Fraude" : "Fraud") : (lang === "ar" ? "ملغى" : lang === "fr" ? "Révoqué" : "Revoked")}
                            </button>
                        ))}
                    </div>
                </div>
                <svg viewBox="0 0 100 100" className="w-full h-auto relative z-10" preserveAspectRatio="xMidYMid meet">
                    <polygon points={OUTLINE} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.28)" strokeWidth="0.35" strokeDasharray="1.5 1.5" strokeLinejoin="round" />
                    {data.map((g) => {
                        const v = metric(g);
                        const r = v === 0 ? 1.2 : 1.5 + (v / max) * 5.5;
                        const isHover = hover?.code === g.code;
                        return (
                            <g key={g.code} onMouseEnter={() => setHover(g)} onMouseLeave={() => setHover(null)} onClick={() => nav(`/app/applications?gov=${g.code}`)} style={{ cursor: "pointer" }} data-testid={`gov-${g.code}`}>
                                <circle cx={g.x} cy={g.y} r={r + 2} fill={`${modeColor}22`} />
                                <circle cx={g.x} cy={g.y} r={r} fill={modeColor} opacity={isHover ? 1 : 0.85} />
                                {mode === "all" && g.fraud_flags > 0 && (
                                    <circle cx={g.x + r * 0.7} cy={g.y - r * 0.7} r={0.9} fill="#f59e0b" stroke="#fff" strokeWidth="0.2" />
                                )}
                                <text x={g.x} y={g.y + r + 2.6} textAnchor="middle" fontSize="2.1" fill="rgba(255,255,255,0.85)" fontFamily="ui-monospace,monospace">
                                    {nameOf(g)}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>

            <div className="space-y-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold">{lang === "ar" ? "التفاصيل" : lang === "fr" ? "Détails" : "Detail"}{hover ? "" : (lang === "ar" ? " · انقر على أي فقاعة" : lang === "fr" ? " · cliquez une bulle" : " · click any bubble")}</div>
                {(hover ? [hover] : data.slice().sort((a, b) => b.registrations - a.registrations).slice(0, 5)).map((g) => (
                    <div key={g.code} className="border border-border rounded-sm p-3 bg-card cursor-pointer hover:border-primary transition-colors" onClick={() => nav(`/app/applications?gov=${g.code}`)} data-testid={`gov-detail-${g.code}`}>
                        <div className="flex items-baseline justify-between">
                            <div className="font-semibold">{nameOf(g)}</div>
                            <div className="text-[10px] mono text-muted-foreground">{g.code}</div>
                        </div>
                        <div className="mt-2 text-xs mono space-y-1">
                            <div className="flex items-baseline justify-between gap-2"><span className="text-muted-foreground whitespace-nowrap">{t("totalApps")}</span><b>{g.registrations}</b></div>
                            <div className="flex items-baseline justify-between gap-2"><span className="text-muted-foreground whitespace-nowrap">{t("pendingReview")}</span><b>{g.pending}</b></div>
                            <div className="flex items-baseline justify-between gap-2"><span className="text-muted-foreground whitespace-nowrap">{t("activePlates")}</span><b>{g.active_plates}</b></div>
                            <div className="flex items-baseline justify-between gap-2"><span className="text-muted-foreground whitespace-nowrap">{t("revokedPlates")}</span><b>{g.revoked_plates}</b></div>
                            <div className="flex items-baseline justify-between gap-2"><span className="text-muted-foreground whitespace-nowrap">{t("fraudFlags")}</span><b className={g.fraud_flags > 0 ? "text-primary" : ""}>{g.fraud_flags}</b></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
